import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { getAuthToken, unpackAuthToken } from '~/helpers/sessionHelpers'
import { RouterOutput } from '~/services/trpc'
import { OrganizationBranding } from '~/stores/stores.types'

import useCurrentOrganization from './useCurrentOrganization'

type OrganizationHistory = Record<
  string,
  {
    name: string
    branding?: OrganizationBranding | null
    activeSession: boolean
  }
>

const ORG_HISTORY_COOKIE_NAME = 'happitu-organization-history'
const upsertOrganizationHistory = (
  organization: RouterOutput['organizations']['getCurrentOrg'],
): Record<ID, { name: string; branding?: OrganizationBranding | null }> => {
  const orgHistory = JSON.parse(Cookies.get(ORG_HISTORY_COOKIE_NAME) || '{}')
  const updatedOrgList = {
    ...orgHistory,
    [organization.id]: {
      name: organization.name,
      branding: organization.branding,
    },
  }
  Cookies.set(ORG_HISTORY_COOKIE_NAME, JSON.stringify(updatedOrgList), { expires: 5000 })

  return updatedOrgList
}

const useCurrentOrganizationHistory = (): OrganizationHistory => {
  const organization = useCurrentOrganization()
  const [availableOrganizations, setAvailableOrganizations] =
    useState<OrganizationHistory>({})

  useEffect(() => {
    const organizationHistory = upsertOrganizationHistory(organization)
    const token = getAuthToken()

    if (token) {
      const unpackedToken = unpackAuthToken(token)
      if (unpackedToken) {
        setAvailableOrganizations(
          Object.keys(organizationHistory).reduce((acc, orgId) => {
            const activeOrgSessions = unpackedToken.oui || {}
            return {
              ...acc,
              [orgId]: {
                name: organizationHistory[orgId].name,
                branding: organizationHistory[orgId].branding,
                activeSession: !!activeOrgSessions[orgId],
              },
            }
          }, {}),
        )
        return
      }
    }
  }, [organization])

  return availableOrganizations
}

export default useCurrentOrganizationHistory
